import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import adminService from '@/services/adminService';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft,
  User,
  TrendingUp,
  FileCheck,
  DollarSign,
  Heart,
  Eye,
  Shield,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Globe,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  UserCheck,
  UserX,
  Plus,
  Minus,
  ExternalLink,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const ROLE_LABELS = {
  investor: { label: 'Yatırımcı', color: 'bg-green-100 text-green-800' },
  staff: { label: 'Personel', color: 'bg-blue-100 text-blue-800' },
  admin: { label: 'Admin', color: 'bg-red-100 text-red-800' },
};

const INVESTMENT_STATUS_LABELS = {
  interested: { label: 'İlgileniyor', color: 'bg-blue-100 text-blue-800' },
  offer_made: { label: 'Teklif Yapıldı', color: 'bg-yellow-100 text-yellow-800' },
  negotiating: { label: 'Müzakere', color: 'bg-orange-100 text-orange-800' },
  contract_signed: { label: 'Sözleşme İmzalandı', color: 'bg-purple-100 text-purple-800' },
  deposit_paid: { label: 'Depozito Ödendi', color: 'bg-indigo-100 text-indigo-800' },
  completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'İptal', color: 'bg-red-100 text-red-800' },
};

const DOC_STATUS_LABELS = {
  pending: { label: 'Bekliyor', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  verified: { label: 'Onaylı', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { label: 'Reddedildi', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const DOC_TYPE_LABELS = {
  passport: 'Pasaport',
  residence_permit: 'Oturma İzni',
  purchase_contract: 'Satış Sözleşmesi',
  tapu: 'Tapu',
  tax_certificate: 'Vergi Belgesi',
  military_permit: 'Askerlik İzni',
  power_of_attorney: 'Vekaletname',
  bank_statement: 'Banka Ekstresi',
  other: 'Diğer',
};

const TX_TYPE_LABELS = {
  deposit: 'Depozito',
  installment: 'Taksit',
  final_payment: 'Son Ödeme',
  refund: 'İade',
  commission: 'Komisyon',
  tax: 'Vergi',
  other: 'Diğer',
};

const TX_STATUS_LABELS = {
  pending: { label: 'Bekliyor', color: 'bg-yellow-100 text-yellow-800' },
  completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-800' },
  failed: { label: 'Başarısız', color: 'bg-red-100 text-red-800' },
  cancelled: { label: 'İptal', color: 'bg-gray-100 text-gray-800' },
};

/**
 * Admin Kullanıcı Detay Sayfası
 * Kullanıcının tüm bilgileri, yatırımları, belgeleri, işlemleri, favorileri ve görüntüleme geçmişi.
 */
export default function AdminUserDetailPage() {
  const { id: userId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [userDetail, setUserDetail] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [propertyViews, setPropertyViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Dialog state
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docStatus, setDocStatus] = useState('');
  const [docNotes, setDocNotes] = useState('');
  const [invDialogOpen, setInvDialogOpen] = useState(false);
  const [selectedInv, setSelectedInv] = useState(null);
  const [invStatus, setInvStatus] = useState('');
  const [invNotes, setInvNotes] = useState('');

  useEffect(() => {
    loadUserData();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserData = async () => {
    setLoading(true);
    const [detailRes, invRes, docRes, txRes, favRes, viewRes] = await Promise.all([
      adminService.getUserDetail(userId),
      adminService.getUserInvestments(userId),
      adminService.getUserDocuments(userId),
      adminService.getUserTransactions(userId),
      adminService.getUserFavorites(userId),
      adminService.getUserPropertyViews(userId),
    ]);

    if (detailRes.data) setUserDetail(detailRes.data);
    if (invRes.data) setInvestments(invRes.data);
    if (docRes.data) setDocuments(docRes.data);
    if (txRes.data) setTransactions(txRes.data);
    if (favRes.data) setFavorites(favRes.data);
    if (viewRes.data) setPropertyViews(viewRes.data);

    setLoading(false);
  };

  const handleToggleStatus = async () => {
    const currentStatus = userDetail?.user_profile?.is_active;
    const { error } = await adminService.updateUserStatus(userId, !currentStatus);
    if (error) {
      toast({ title: 'Hata', description: 'Durum güncellenemedi', variant: 'destructive' });
    } else {
      toast({ title: 'Başarılı', description: `Kullanıcı ${!currentStatus ? 'aktif' : 'pasif'} yapıldı` });
      loadUserData();
    }
  };

  const handleAddRole = async (role) => {
    const { error } = await adminService.addUserRole(userId, role);
    if (error) {
      toast({ title: 'Hata', description: 'Rol eklenemedi', variant: 'destructive' });
    } else {
      toast({ title: 'Başarılı', description: `${ROLE_LABELS[role]?.label || role} rolü eklendi` });
      loadUserData();
    }
  };

  const handleRemoveRole = async (role) => {
    const { error } = await adminService.removeUserRole(userId, role);
    if (error) {
      toast({ title: 'Hata', description: 'Rol kaldırılamadı', variant: 'destructive' });
    } else {
      toast({ title: 'Başarılı', description: `${ROLE_LABELS[role]?.label || role} rolü kaldırıldı` });
      loadUserData();
    }
  };

  const handleVerifyDocument = async () => {
    if (!selectedDoc || !docStatus) return;
    const { error } = await adminService.verifyDocument(selectedDoc.id, docStatus, docNotes || null);
    if (error) {
      toast({ title: 'Hata', description: 'Belge güncellenemedi', variant: 'destructive' });
    } else {
      toast({ title: 'Başarılı', description: 'Belge durumu güncellendi' });
      setDocDialogOpen(false);
      loadUserData();
    }
  };

  const handleUpdateInvestment = async () => {
    if (!selectedInv || !invStatus) return;
    const { error } = await adminService.updateInvestmentStatus(selectedInv.id, invStatus, invNotes || null);
    if (error) {
      toast({ title: 'Hata', description: 'Yatırım güncellenemedi', variant: 'destructive' });
    } else {
      toast({ title: 'Başarılı', description: 'Yatırım durumu güncellendi' });
      setInvDialogOpen(false);
      loadUserData();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency', currency: 'GBP', maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!userDetail) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="pt-6 text-center">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-red-500" />
          <p className="text-red-600">Kullanıcı bulunamadı</p>
          <Button variant="outline" onClick={() => navigate('/admin/users')} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Kullanıcılara Dön
          </Button>
        </CardContent>
      </Card>
    );
  }

  const up = userDetail.user_profile || {};
  const ip = userDetail.investor_profile || {};
  const sp = userDetail.staff_profile || {};
  const auth = userDetail.auth_info || {};
  const roles = up.roles?.length > 0 ? up.roles : up.role ? [up.role] : [];
  const allRoles = ['investor', 'staff', 'admin'];
  const missingRoles = allRoles.filter((r) => !roles.includes(r));

  return (
    <div className="space-y-6">
      {/* Geri Butonu */}
      <Button variant="ghost" onClick={() => navigate('/admin/users')} className="mb-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Kullanıcılara Dön
      </Button>

      {/* Kullanıcı Profil Kartı */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <div className="w-20 h-20 bg-gradient-to-br from-[#D4AF37] to-[#B5952F] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {up.full_name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            </div>

            {/* Bilgiler */}
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-2xl font-bold text-[#0A192F]">{up.full_name || 'İsimsiz'}</h2>
                <Badge variant={up.is_active ? 'default' : 'secondary'} className={up.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                  {up.is_active ? 'Aktif' : 'Pasif'}
                </Badge>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="w-4 h-4" /> {auth.email || '-'}
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="w-4 h-4" /> {up.phone || '-'}
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-4 h-4" /> Kayıt: {formatDate(auth.created_at)}
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="w-4 h-4" /> Son Giriş: {formatDate(auth.last_sign_in_at)}
                </div>
                {ip.country && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-4 h-4" /> {ip.city ? `${ip.city}, ` : ''}{ip.country}
                  </div>
                )}
                {ip.nationality && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Globe className="w-4 h-4" /> {ip.nationality}
                  </div>
                )}
              </div>

              {/* Roller */}
              <div className="flex items-center gap-2 flex-wrap">
                <Shield className="w-4 h-4 text-muted-foreground" />
                {roles.map((r) => {
                  const cfg = ROLE_LABELS[r] || { label: r, color: 'bg-gray-100 text-gray-800' };
                  return (
                    <Badge key={r} className={`${cfg.color} text-xs`}>
                      {cfg.label}
                      <button
                        onClick={() => handleRemoveRole(r)}
                        className="ml-1 hover:text-red-600"
                        title="Rolü kaldır"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                    </Badge>
                  );
                })}
                {missingRoles.map((r) => {
                  const cfg = ROLE_LABELS[r];
                  return (
                    <Button key={r} variant="outline" size="sm" className="h-6 text-xs" onClick={() => handleAddRole(r)}>
                      <Plus className="w-3 h-3 mr-1" /> {cfg.label}
                    </Button>
                  );
                })}
              </div>

              {/* Hızlı İstatistikler */}
              <div className="flex gap-4 flex-wrap text-sm">
                <span className="flex items-center gap-1"><TrendingUp className="w-4 h-4 text-green-600" /> {userDetail.investments_count} yatırım</span>
                <span className="flex items-center gap-1"><FileCheck className="w-4 h-4 text-blue-600" /> {userDetail.documents_count} belge</span>
                <span className="flex items-center gap-1"><DollarSign className="w-4 h-4 text-emerald-600" /> {formatCurrency(userDetail.total_invested)}</span>
                <span className="flex items-center gap-1"><Heart className="w-4 h-4 text-red-600" /> {userDetail.favorites_count} favori</span>
              </div>
            </div>

            {/* Aksiyonlar */}
            <div className="flex-shrink-0 flex flex-col gap-2">
              <Button
                variant={up.is_active ? 'destructive' : 'default'}
                size="sm"
                onClick={handleToggleStatus}
              >
                {up.is_active ? <UserX className="w-4 h-4 mr-1" /> : <UserCheck className="w-4 h-4 mr-1" />}
                {up.is_active ? 'Pasif Yap' : 'Aktif Yap'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sekmeler */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6">
          <TabsTrigger value="overview" className="text-xs">Genel</TabsTrigger>
          <TabsTrigger value="investments" className="text-xs">Yatırımlar ({investments.length})</TabsTrigger>
          <TabsTrigger value="documents" className="text-xs">Belgeler ({documents.length})</TabsTrigger>
          <TabsTrigger value="transactions" className="text-xs">İşlemler ({transactions.length})</TabsTrigger>
          <TabsTrigger value="favorites" className="text-xs">Favoriler ({favorites.length})</TabsTrigger>
          <TabsTrigger value="views" className="text-xs">Görüntüleme ({propertyViews.length})</TabsTrigger>
        </TabsList>

        {/* GENEL BİLGİ */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Yatırımcı Profili */}
            {ip && Object.keys(ip).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Yatırımcı Profili</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Bütçe:</span><span>{ip.investment_budget ? formatCurrency(ip.investment_budget) : '-'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Tercih Lokasyon:</span><span>{ip.preferred_locations?.join(', ') || '-'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Tercih Tip:</span><span>{ip.preferred_property_types?.join(', ') || '-'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Yatırım Hedefi:</span><span>{ip.investment_goal || '-'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Doğum Tarihi:</span><span>{ip.date_of_birth || '-'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Doğrulanmış:</span>
                    <Badge variant={ip.is_verified ? 'default' : 'secondary'} className={ip.is_verified ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                      {ip.is_verified ? 'Evet' : 'Hayır'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Staff Profili */}
            {sp && Object.keys(sp).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Personel Profili</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Departman:</span><span>{sp.department || '-'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Pozisyon:</span><span>{sp.position || '-'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">İşe Başlama:</span><span>{sp.hire_date || '-'}</span></div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* YATIRIMLAR */}
        <TabsContent value="investments" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {investments.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Yatırım bulunamadı</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Gayrimenkul</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead>Teklif</TableHead>
                        <TableHead>Tapu</TableHead>
                        <TableHead>Tarih</TableHead>
                        <TableHead className="text-right">İşlem</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {investments.map((inv) => {
                        const stCfg = INVESTMENT_STATUS_LABELS[inv.status] || { label: inv.status, color: 'bg-gray-100 text-gray-800' };
                        return (
                          <TableRow key={inv.id}>
                            <TableCell className="font-medium max-w-[200px] truncate">
                              {inv.property?.title_tr || inv.property_id?.slice(0, 8)}
                            </TableCell>
                            <TableCell>
                              <Badge className={`${stCfg.color} text-xs`}>{stCfg.label}</Badge>
                            </TableCell>
                            <TableCell>{inv.offer_amount ? formatCurrency(inv.offer_amount) : '-'}</TableCell>
                            <TableCell>{inv.tapu_status || '-'}</TableCell>
                            <TableCell className="text-xs">{formatDate(inv.updated_at)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedInv(inv);
                                  setInvStatus(inv.status);
                                  setInvNotes(inv.notes || '');
                                  setInvDialogOpen(true);
                                }}
                              >
                                Düzenle
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* BELGELER */}
        <TabsContent value="documents" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {documents.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Belge bulunamadı</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Dosya</TableHead>
                        <TableHead>Tür</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead>Boyut</TableHead>
                        <TableHead>Yükleme</TableHead>
                        <TableHead className="text-right">İşlem</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {documents.map((doc) => {
                        const stCfg = DOC_STATUS_LABELS[doc.status] || { label: doc.status, color: 'bg-gray-100 text-gray-800' };
                        const StatusIcon = stCfg.icon || Clock;
                        return (
                          <TableRow key={doc.id}>
                            <TableCell className="font-medium max-w-[200px] truncate">
                              <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline">
                                {doc.file_name}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </TableCell>
                            <TableCell>{DOC_TYPE_LABELS[doc.document_type] || doc.document_type}</TableCell>
                            <TableCell>
                              <Badge className={`${stCfg.color} text-xs`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {stCfg.label}
                              </Badge>
                            </TableCell>
                            <TableCell>{doc.file_size ? `${(doc.file_size / 1024).toFixed(0)} KB` : '-'}</TableCell>
                            <TableCell className="text-xs">{formatDate(doc.uploaded_at)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedDoc(doc);
                                  setDocStatus(doc.status);
                                  setDocNotes(doc.notes || '');
                                  setDocDialogOpen(true);
                                }}
                              >
                                Doğrula
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* İŞLEMLER */}
        <TabsContent value="transactions" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {transactions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">İşlem bulunamadı</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tür</TableHead>
                        <TableHead>Tutar</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead>Ödeme Yöntemi</TableHead>
                        <TableHead>Referans</TableHead>
                        <TableHead>Tarih</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((tx) => {
                        const stCfg = TX_STATUS_LABELS[tx.status] || { label: tx.status, color: 'bg-gray-100 text-gray-800' };
                        return (
                          <TableRow key={tx.id}>
                            <TableCell>{TX_TYPE_LABELS[tx.transaction_type] || tx.transaction_type}</TableCell>
                            <TableCell className="font-medium">{formatCurrency(tx.amount)}</TableCell>
                            <TableCell>
                              <Badge className={`${stCfg.color} text-xs`}>{stCfg.label}</Badge>
                            </TableCell>
                            <TableCell>{tx.payment_method || '-'}</TableCell>
                            <TableCell className="text-xs">{tx.reference_number || '-'}</TableCell>
                            <TableCell className="text-xs">{formatDate(tx.transaction_date)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* FAVORİLER */}
        <TabsContent value="favorites" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {favorites.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Favori bulunamadı</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {favorites.map((fav) => (
                    <Card key={fav.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="pt-4">
                        <h4 className="font-medium text-sm truncate">{fav.property?.title_tr || '-'}</h4>
                        <p className="text-xs text-muted-foreground mt-1">{fav.property?.location_tr || '-'}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-sm font-bold text-[#D4AF37]">
                            {fav.property?.price_gbp ? formatCurrency(fav.property.price_gbp) : '-'}
                          </span>
                          <Badge variant="secondary" className="text-xs">{fav.property?.type || '-'}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          Ekleme: {formatDate(fav.created_at)}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* GÖRÜNTÜLEME GEÇMİŞİ */}
        <TabsContent value="views" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {propertyViews.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Görüntüleme geçmişi bulunamadı</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Gayrimenkul</TableHead>
                        <TableHead>Konum</TableHead>
                        <TableHead>Tür</TableHead>
                        <TableHead>Fiyat</TableHead>
                        <TableHead>Görüntüleme</TableHead>
                        <TableHead>Son Görüntüleme</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {propertyViews.map((view) => (
                        <TableRow key={view.id}>
                          <TableCell className="font-medium max-w-[200px] truncate">
                            {view.property?.title_tr || '-'}
                          </TableCell>
                          <TableCell className="text-xs">{view.property?.location_tr || '-'}</TableCell>
                          <TableCell><Badge variant="secondary" className="text-xs">{view.property?.type || '-'}</Badge></TableCell>
                          <TableCell>{view.property?.price_gbp ? formatCurrency(view.property.price_gbp) : '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{view.view_count}x</Badge>
                          </TableCell>
                          <TableCell className="text-xs">{formatDate(view.viewed_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Belge Doğrulama Dialog */}
      <Dialog open={docDialogOpen} onOpenChange={setDocDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Belge Doğrulama</DialogTitle>
            <DialogDescription>
              {selectedDoc?.file_name} - {DOC_TYPE_LABELS[selectedDoc?.document_type] || selectedDoc?.document_type}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Durum</label>
              <Select value={docStatus} onValueChange={setDocStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Bekliyor</SelectItem>
                  <SelectItem value="verified">Onayla</SelectItem>
                  <SelectItem value="rejected">Reddet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Notlar</label>
              <Textarea
                value={docNotes}
                onChange={(e) => setDocNotes(e.target.value)}
                placeholder="Admin notları..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocDialogOpen(false)}>İptal</Button>
            <Button onClick={handleVerifyDocument}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Yatırım Düzenleme Dialog */}
      <Dialog open={invDialogOpen} onOpenChange={setInvDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yatırım Durumu Güncelle</DialogTitle>
            <DialogDescription>
              {selectedInv?.property?.title_tr || 'Yatırım'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Durum</label>
              <Select value={invStatus} onValueChange={setInvStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="interested">İlgileniyor</SelectItem>
                  <SelectItem value="offer_made">Teklif Yapıldı</SelectItem>
                  <SelectItem value="negotiating">Müzakere</SelectItem>
                  <SelectItem value="contract_signed">Sözleşme İmzalandı</SelectItem>
                  <SelectItem value="deposit_paid">Depozito Ödendi</SelectItem>
                  <SelectItem value="completed">Tamamlandı</SelectItem>
                  <SelectItem value="cancelled">İptal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Notlar</label>
              <Textarea
                value={invNotes}
                onChange={(e) => setInvNotes(e.target.value)}
                placeholder="Admin notları..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvDialogOpen(false)}>İptal</Button>
            <Button onClick={handleUpdateInvestment}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Toaster />
    </div>
  );
}
